import { ContentState, convertFromRaw, convertToRaw, RawDraftContentState } from 'draft-js';
import { isEmpty, isNumber, isString } from 'lodash';
import { CellData, TableContentData } from '../../../../common/table/TableTypes';
import {
    isCellValueInDraftFormat,
    parseCellContentString,
} from '../../../../common/table/utils/tableCellContentStringUtils';
import { cellCanBeRichFormatted } from '../utils/dataUtils/cell/tableCellTypeModelUtils';
import { convertDraftContentToTiptap } from '../../../../common/tiptap/conversion/editorConversion/toTiptap/convertDraftContentToTiptap';
import rawGetText from '../../../../common/utils/editor/rawUtils/rawGetText';
import { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import applyInlineStylesThroughoutContent from '../../../components/editor/customRichUtils/applyInlineStylesThroughoutContent';

/**
 * Adds the empty entity map object to draftContent.
 *
 * This is required, otherwise the Draft.js decodeRawEntityMap with throw an error.
 */
const normalizeDraftContentEntityMap = (draftContent: RawDraftContentState): RawDraftContentState =>
    draftContent.entityMap ? draftContent : { ...draftContent, entityMap: {} };

/**
 * Update the cell data with safe draft content (i.e. draft content that contains an entityMap object).
 */
const normalizeTableCellDraftContent = (cellData: CellData): CellData => {
    if (!cellData.draftContent) return cellData;

    const normalizedDraftContent = normalizeDraftContentEntityMap(cellData.draftContent);
    if (normalizedDraftContent === cellData.draftContent) return cellData;

    return { ...cellData, draftContent: normalizedDraftContent };
};

/**
 * Restores the Draft.js defaults that can be stripped from persisted V2 table cells,
 * such as an empty `entityMap`, before the content is rehydrated into Draft.js.
 *
 * Mongo will minimise empty objects, so if the entityMap = {}, we need to restore it.
 */
export const normalizeTableContentDataDraftContent = (tableContentData: TableContentData): TableContentData =>
    tableContentData.map((row) => row.map(normalizeTableCellDraftContent));

/**
 * Converts table content data from version 1 to version 2 by parsing Draft.JS content strings
 * into their corresponding Draft.JS objects and extracting the raw text.
 */
export const convertTableContentDataToV2 = (tableContentData: TableContentData): TableContentData => {
    return tableContentData.map((row) =>
        row.map((cellData) => {
            if (!cellData.value || !isCellValueInDraftFormat(cellData.value)) return cellData;

            const draftContent = parseCellContentString(cellData.value);
            if (isString(draftContent) || isNumber(draftContent)) return cellData;

            return { ...cellData, draftContent, value: rawGetText(draftContent) };
        }),
    );
};

/**
 * Apply cell-level text styling to draftContent before converting it to tiptapContent.
 *
 * This is so that tiptap do not have to do extra pre-rendering processing, as the text styling is already
 * included in tiptapContent.
 */
const prepareDraftContentForV3Conversion = (cellData: CellData): RawDraftContentState | undefined => {
    let draftContent = cellData.draftContent;

    if (!draftContent) {
        const shouldApplyTextStyling = cellCanBeRichFormatted(cellData) && !isEmpty(cellData.textStyle);
        if (!shouldApplyTextStyling) return;

        draftContent = convertToRaw(ContentState.createFromText(cellData.value || ''));
    }

    const contentState = convertFromRaw(draftContent);
    const styledContentState = applyInlineStylesThroughoutContent(contentState, cellData.textStyle);
    return convertToRaw(styledContentState);
};

/**
 * Converts table content data from version 2 to version 3 by replacing Draft.js rich text payloads
 * with Tiptap content while preserving the existing plain-text `value`.
 */
export const convertTableContentDataToV3 = (tableContentData: TableContentData): TableContentData => {
    return tableContentData.map((row) =>
        row.map((cellData) => {
            if (cellData.tiptapContent) return cellData;

            const draftContent = prepareDraftContentForV3Conversion(cellData);
            if (!draftContent) return cellData;

            const tiptapContent = convertDraftContentToTiptap(draftContent) as TiptapContent;

            return { ...cellData, tiptapContent };
        }),
    );
};
